import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useRecoilValue } from "recoil";

import { Avatar } from "../league/shared/Avatar";
import * as SBService from "../../helpers/sportbookService";
import { getAssetFullUrl } from "../../helpers/utils";
import { swissRefreshState, userState } from "../../states/common";
import logoIcon from "../../statics/images/logo.png";
import "../league/shared/league.css";

/**
 * "Bây giờ tôi phải làm gì?" — khối đầu tiên của trang chủ.
 *
 * Trước đây muốn biết còn việc gì phải mở từng giải một. Người chơi vài giải cùng
 * lúc thì việc nằm rải ở mấy trang khác nhau, và một trận chờ xác nhận kết quả có
 * thể nằm đó cả tuần mà không ai nhớ.
 *
 * Đặt TRÊN CÙNG vì đây là thứ duy nhất trên trang chủ cần người dùng làm gì đó ngay;
 * mấy khối dưới đều là thông tin để xem.
 *
 * Thẻ dùng lại đúng thông tin của tab Thi đấu (đối thủ, hạng, điểm, chạm, bàn) vì
 * server trả về nguyên khối currentMatch đó — không dựng lại ở đây, nếu không hai
 * nơi mô tả cùng một trận theo hai kiểu.
 */

type Viec = "thi_dau" | "nop_ket_qua" | "xac_nhan_ket_qua";

type ItemViec = {
  viec: Viec;
  league: { id: string; name: string; logo: string | null };
  match: any;
};

/** Nhãn và màu theo việc. Ba việc, ba mức gấp khác nhau. */
const NHAN: Record<Viec, { chip: string; nut: string; lop: string }> = {
  thi_dau: { chip: "Cần thi đấu", nut: "Vào trận", lop: "vc-thidau" },
  nop_ket_qua: { chip: "Chờ nhập tỉ số", nut: "Nhập kết quả", lop: "vc-nop" },
  xac_nhan_ket_qua: { chip: "Chờ bạn xác nhận", nut: "Xác nhận", lop: "vc-xacnhan" },
};

const ViecCanLam = () => {
  const user = useRecoilValue(userState);
  const nhipNapLai = useRecoilValue(swissRefreshState);
  const navigate = useNavigate();
  const [items, setItems] = useState<ItemViec[]>([]);

  const load = useCallback(async () => {
    if (!user?.id) {
      setItems([]);

      return;
    }
    try {
      /* Hai thể thức, hai endpoint, MỘT danh sách. Giải Swiss và giải Đấu kép đều có
         việc chờ cơ thủ, và người chơi không phân biệt theo thể thức - họ chỉ cần
         biết còn việc gì. Hỏng một bên thì vẫn hiện bên kia: dùng allSettled chứ
         không all, vì một giải Đấu kép chưa chạy SQL sẽ làm cả khối biến mất. */
      const [swiss, de] = await Promise.allSettled([
        SBService.getSwissMyActions(),
        SBService.getDeMyActions(),
      ]);
      const boc = (r: PromiseSettledResult<any>) => {
        if (r.status !== "fulfilled") {
          return [];
        }
        const data = r.value?.data ?? r.value;

        return Array.isArray(data) ? data : [];
      };
      setItems([...boc(swiss), ...boc(de)]);
    } catch (e) {
      // Khối này là đường tắt, không phải nguồn sự thật: hỏng thì ẩn đi chứ không
      // dựng một thông báo lỗi ngay trên đầu trang chủ. Mọi việc vẫn làm được từ
      // trang giải.
      setItems([]);
    }
  }, [user?.id]);

  useEffect(() => {
    load();
  }, [load, nhipNapLai]);

  /* Cũng tự làm mới như trang giải, và vì đúng lý do đó: đối thủ nhập tỉ số hay ban
     tổ chức sửa kết quả thì không có đường realtime nào báo sang. Chỉ nạp khi tab
     đang hiện, và nạp ngay khi quay lại tab. */
  useEffect(() => {
    const dangXem = () => document.visibilityState === "visible";
    const dinhKy = setInterval(() => {
      if (dangXem()) {
        load();
      }
    }, 20000);
    const khiQuayLai = () => {
      if (dangXem()) {
        load();
      }
    };
    document.addEventListener("visibilitychange", khiQuayLai);

    return () => {
      clearInterval(dinhKy);
      document.removeEventListener("visibilitychange", khiQuayLai);
    };
  }, [load]);

  if (items.length === 0) {
    return null;
  }

  return (
    <section className="section biadi biadi-khoi">
      <div className="section-head">
        <h2>CẦN BẠN THỰC HIỆN</h2>
      </div>

      <div className="hero-carousel">
        {items.map((item) => {
          const tran = item.match || {};
          const doiThu = tran.opponent;
          const nhan = NHAN[item.viec] ?? NHAN.thi_dau;
          /* roundLabel là nhãn do server đặt sẵn (nhánh kép không có "vòng" theo nghĩa
             Swiss: ô ở Vòng 3 nhánh thua và ô bán kết là hai chuyện khác hẳn). Thể
             thức nào không gửi thì giữ nguyên cách gọi tên cũ. */
          const vongDau =
            tran.roundLabel ||
            (tran.stage === "playoff" ? tran.roundName || "Vòng loại trực tiếp" : `Vòng ${tran.round}`);

          return (
            <div
              key={`${item.league.id}-${tran.scheduleId}`}
              className={`ui-card viec-card ${nhan.lop}`}
              onClick={() => navigate(`/league/${item.league.id}`)}
            >
              {/* Trận thuộc giải nào — không có dòng này thì hai trận của hai giải
                  khác nhau trông y hệt nhau. */}
              <div className="viec-giai">
                <img
                  alt=""
                  src={item.league.logo ? getAssetFullUrl(item.league.logo) : logoIcon}
                />
                <span className="ten">{item.league.name}</span>
                <span className={`viec-chip ${nhan.lop}`}>{nhan.chip}</span>
              </div>

              <div className="viec-doithu">
                <Avatar
                  src={doiThu?.avatar}
                  name={doiThu?.name || "Đối thủ"}
                  rankClass={doiThu?.rankClass}
                  score={doiThu?.score}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="nm">{doiThu?.name || "Chưa có đối thủ"}</div>
                  <div className="tiny">
                    {doiThu?.rankClass ? <b>{doiThu.rankClass}</b> : null}
                    {doiThu?.rankClass && doiThu?.score !== null && doiThu?.score !== undefined
                      ? " • "
                      : null}
                    {doiThu?.score !== null && doiThu?.score !== undefined
                      ? `${Number(doiThu.score).toLocaleString("vi-VN")} điểm`
                      : null}
                  </div>
                </div>
              </div>

              <div className="viec-chan">
                <span className="tiny">
                  {vongDau}
                  {tran.race ? ` • Chạm ${tran.race}` : ""}
                  {tran.table ? ` • Bàn ${tran.table}` : ""}
                </span>
                <span className="btn btn-sm btn-primary">{nhan.nut}</span>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default ViecCanLam;
